import { Component, Input, OnInit, OnDestroy, Optional, Self, HostBinding, ElementRef, ChangeDetectionStrategy, Output, EventEmitter } from '@angular/core';
import { NgControl, ControlValueAccessor } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MapDesignerDialogComponent } from './components/map-designer-dialog/map-designer-dialog.component';
import { MapDesignerReferenceLayer } from './models/map-designer-reference-layer';

@Component({
    selector: 'app-map-designer',
    templateUrl: './map-designer.component.html',
    styleUrls: ['./map-designer.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: MapDesignerComponent }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MapDesignerComponent implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string> {
  static nextId = 0;
  @HostBinding() id = `map-designer-${MapDesignerComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  stateChanges = new Subject<void>();
  focused = false;
  controlType = 'map-designer';

  private _readonlyGeoJson: string[] | null = null;
  geoJsonString: string = ''; // JSON string representation
  geoJsonForm: FormGroup;

  @Input() name: string = ''; // New input for name property
  @Input() overlayImage: string = '';
  @Input() referenceLayers: MapDesignerReferenceLayer[] = [];
  @Output() overlayImageChange = new EventEmitter<string>();

  @Input()
  get readonlyGeoJson(): string[] | null {
    return this._readonlyGeoJson;
  }
  set readonlyGeoJson(val: string[] | null) {
    this._readonlyGeoJson = val;
    this.stateChanges.next();
  }

  @Input()
  get value(): string {
    return this.geoJsonString;
  }
  set value(val: string) {
    this.geoJsonString = val;
    this.stateChanges.next();
    this.onChange(this.geoJsonString);
  }

  private _placeholder: string;
  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(plh: string) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  private _required = false;
  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  private _disabled = false;
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(dis: boolean) {
    this._disabled = coerceBooleanProperty(dis);
    this.stateChanges.next();
  }

  @Optional() @Self() public ngControl: NgControl;

  highlightState = false;

  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    private fm: FocusMonitor,
    private elementRef: ElementRef,
    @Optional() @Self() ngControl: NgControl
  ) {
    if (ngControl != null) {
      this.ngControl = ngControl;
      this.ngControl.valueAccessor = this;
    }

    fm.monitor(this.elementRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  ngOnInit(): void {
    this.geoJsonForm = this.fb.group({
      geoJson: [this.geoJsonString]
    });
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elementRef.nativeElement);
  }

  get empty() {
    return !this.value;
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get errorState(): boolean {
    return this.ngControl && this.ngControl.invalid && (this.ngControl.dirty || this.ngControl.touched);
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    // if ((event.target as Element).tagName.toLowerCase() !== 'input') {
    //   this.openDialog();
    // }
  }

  writeValue(value: any): void {
    this.geoJsonString = value;

    if (this.geoJsonForm) {
      this.geoJsonForm.patchValue({ geoJson: this.geoJsonString });
    }

    this.stateChanges.next();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.stateChanges.next();
  }

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  openDialog(): void {
    const dialogRef = this.dialog.open(MapDesignerDialogComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      panelClass: 'map-designer-dialog-panel',
      data: {
        referenceGeoJSON: this.readonlyGeoJson ? this.readonlyGeoJson.map(json => this.parseGeoJson(json)) : [],
        referenceLayers: (this.referenceLayers ?? []).map(reference => ({
          ...reference,
          geoJSON: this.parseGeoJson(reference.geoJSON, reference.name, reference.relation)
        })),
        editableGeoJSON: this.parseGeoJson(this.geoJsonString),
        overlayImage: this.overlayImage,
        name: this.name // passing the name to the dialog
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.status || !result.data) {
        return;
      }

      const serialized = typeof result.data === 'string'
        ? result.data
        : JSON.stringify(result.data, null, 2);

      this.value = serialized;
      if (typeof result.overlayImage === 'string') {
        this.overlayImage = result.overlayImage;
        this.overlayImageChange.emit(result.overlayImage);
      }
      this.geoJsonForm.patchValue({ geoJson: serialized });
      this.onTouched();
      this.stateChanges.next();
    });
  }

  focus() {
    this.focused = true;
    this.stateChanges.next();
  }

  blur() {
    this.focused = false;
    this.onTouched();
    this.stateChanges.next();
  }

  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.geoJsonString = input.value;
    this.onChange(this.geoJsonString);
    this.onTouched();
    this.stateChanges.next();
  }

  private parseGeoJson(
    value: any,
    featureName = this.name,
    referenceRelation?: MapDesignerReferenceLayer['relation']
  ): any {
    if (!value) {
      return this.createEmptyFeatureCollection(featureName);
    }

    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;

      if (parsed?.type === 'FeatureCollection') {
        parsed.features = (parsed.features ?? []).map((feature: any) =>
          this.ensureFeatureHasName(feature, featureName, referenceRelation));
        return parsed;
      }

      if (parsed?.type === 'Feature') {
        return {
          type: 'FeatureCollection',
          features: [this.ensureFeatureHasName(parsed, featureName, referenceRelation)],
          properties: { name: featureName }
        };
      }

      if (Array.isArray(parsed)) {
        return {
          type: 'FeatureCollection',
          features: parsed.map(feature => this.ensureFeatureHasName(feature, featureName, referenceRelation)),
          properties: { name: featureName }
        };
      }
    } catch (error) {
      console.error('Invalid GeoJSON value provided, using default value.', value, error);
    }

    return this.createEmptyFeatureCollection(featureName);
  }

  private ensureFeatureHasName(
    feature: any,
    featureName: string,
    referenceRelation?: MapDesignerReferenceLayer['relation']
  ): any {
    return {
      ...(feature ?? {}),
      properties: {
        ...((feature && feature.properties) ?? {}),
        name: featureName,
        ...(referenceRelation ? {
          __referenceRelation: referenceRelation,
          __referenceName: featureName
        } : {})
      }
    };
  }

  private createEmptyFeatureCollection(featureName = this.name): any {
    return {
      type: 'FeatureCollection',
      features: [],
      properties: {
        name: featureName
      }
    };
  }
}
